import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiJson } from "../lib/queryClient";

type AdminUserSummary = {
  id: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
  authProvider: string;
  needsPasswordSetup: boolean;
  createdAt: string;
  subscription: {
    active: boolean;
    source: "admin" | "manual" | "stripe" | "trial" | "none";
    planName: string | null;
    interval: string | null;
    status: string | null;
  } | null;
  counts: {
    drones: number;
    sessions: number;
    logbook: number;
    glossary: number;
    resources: number;
  };
};

const SUB_SOURCE_LABEL: Record<string, string> = {
  admin: "Admin",
  manual: "Manuell",
  stripe: "Stripe",
  trial: "Test",
  none: "—",
};

function PasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4 shadow-xl">
        <h2 className="text-lg font-semibold text-[var(--color-app-text)]">Neues Passwort für {email}</h2>
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Das Passwort wird nur einmalig angezeigt. Bitte sicher an den Nutzer übergeben.
        </p>
        <div className="flex gap-2 items-center">
          <code className="flex-1 px-3 py-2 bg-[var(--color-app-bg-tertiary)] border border-[var(--color-app-border)] rounded font-mono text-sm break-all">
            {password}
          </code>
          <button
            onClick={copy}
            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded"
          >
            {copied ? "Kopiert" : "Kopieren"}
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-[var(--color-app-bg-tertiary)] hover:bg-[var(--color-app-bg-tertiary)] rounded"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function SetupLinkModal({
  email,
  url,
  expiresAt,
  onClose,
}: {
  email: string;
  url: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyError(true);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-xl w-full p-6 space-y-4 shadow-xl">
        <h2 className="text-lg font-semibold text-[var(--color-app-text)]">
          Einmaliger Setup-Link für {email}
        </h2>
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Teile diesen Link direkt mit dem Nutzer. Er ist 15 Minuten gültig und kann nur einmal
          verwendet werden. Der Token wird nicht per E-Mail verschickt und später nicht erneut
          angezeigt.
        </p>
        <input
          aria-label="Passwort-Setup-Link"
          readOnly
          value={url}
          onFocus={(event) => event.currentTarget.select()}
          className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm font-mono"
        />
        <p className="text-xs text-[var(--color-app-text-secondary)]">
          Gültig bis {new Date(expiresAt).toLocaleString("de-DE")}
        </p>
        {copyError && (
          <p role="status" className="text-sm text-[var(--color-caution)]">
            Kopieren nicht möglich. Markiere den Link im Feld und kopiere ihn manuell.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={copy}
            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded"
          >
            {copied ? "Kopiert" : "Link kopieren"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-[var(--color-app-bg-tertiary)] hover:bg-[var(--color-app-bg-tertiary)] rounded"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [items, setItems] = useState<AdminUserSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pwModal, setPwModal] = useState<{ email: string; password: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState("");
  const [createName, setCreateName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<{
    email: string;
    url: string;
    expiresAt: string;
  } | null>(null);

  async function reload() {
    const data = await apiJson<{ users: AdminUserSummary[] }>("GET", "/api/admin/users");
    setItems(data.users);
  }

  useEffect(() => {
    if (!user?.isAdmin) return;
    reload().catch((e) => setError((e as Error).message));
  }, [user?.isAdmin]);

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  async function toggleRole(u: AdminUserSummary) {
    const next = !u.isAdmin;
    const verb = next ? "zum Admin machen" : "die Admin-Rolle entziehen";
    if (!confirm(`Möchtest du ${u.email} ${verb}?`)) return;
    setBusy(u.id);
    try {
      await apiJson("PATCH", `/api/admin/users/${u.id}/role`, { isAdmin: next });
      await reload();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function resetPassword(u: AdminUserSummary) {
    if (!confirm(`Passwort für ${u.email} zurücksetzen?`)) return;
    setBusy(u.id);
    try {
      const data = await apiJson<{ newPassword: string }>(
        "POST",
        `/api/admin/users/${u.id}/reset-password`,
      );
      setPwModal({ email: u.email, password: data.newPassword });
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function buildSetupUrl(token: string): string {
    return `${window.location.origin}/setup-password#token=${encodeURIComponent(token)}`;
  }

  async function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    setBusy("create");
    try {
      const data = await apiJson<{
        user: { email: string };
        setupToken: string;
        expiresAt: string;
      }>("POST", "/api/admin/users", {
        email: createEmail.trim(),
        name: createName.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateEmail("");
      setCreateName("");
      setSetupLink({
        email: data.user.email,
        url: buildSetupUrl(data.setupToken),
        expiresAt: data.expiresAt,
      });
      reload().catch((e) =>
        alert(`Nutzer wurde angelegt, aber die Liste konnte nicht aktualisiert werden: ${(e as Error).message}`),
      );
    } catch (e) {
      setCreateError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function issueSetupLink(u: AdminUserSummary) {
    if (
      !confirm(
        `Einen neuen Setup-Link für ${u.email} erstellen? Ein bisheriger Link wird dadurch ungültig.`,
      )
    ) {
      return;
    }
    setBusy(u.id);
    try {
      const data = await apiJson<{
        user: { email: string };
        setupToken: string;
        expiresAt: string;
      }>("POST", `/api/admin/users/${u.id}/setup-link`);
      setSetupLink({
        email: data.user.email,
        url: buildSetupUrl(data.setupToken),
        expiresAt: data.expiresAt,
      });
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function deleteUser(u: AdminUserSummary) {
    if (
      !confirm(
        `Nutzer ${u.email} und ALLE zugehörigen Daten (Drohnen, Flüge, Logbuch, Profil, …) endgültig löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`,
      )
    )
      return;
    setBusy(u.id);
    try {
      await apiJson("DELETE", `/api/admin/users/${u.id}`);
      await reload();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (!user?.isAdmin) {
    return (
      <div className="p-6 bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] text-[var(--color-nogo)] rounded-md">
        Diese Seite ist nur für Admins.
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] text-[var(--color-nogo)] rounded-md">
        {error}
      </div>
    );
  }
  if (!items || !filtered) return <div className="text-[var(--color-app-text-secondary)]">Lädt …</div>;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-app-text)]">Admin · Nutzerverwaltung</h1>
          <p className="text-sm text-[var(--color-app-text-secondary)]">
            Rollen, Passwörter und Konten verwalten.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy === "create"}
            onClick={() => {
              setCreateError(null);
              setCreateOpen(true);
            }}
            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded disabled:opacity-50"
          >
            Neuen Nutzer anlegen
          </button>
          <input
            type="search"
            placeholder="Suche nach E-Mail oder Name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm w-72"
          />
        </div>
      </header>

      <div className="overflow-x-auto bg-white border border-[var(--color-app-border)] rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-app-bg-secondary)] text-left text-[var(--color-app-text-secondary)] text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2">Nutzer</th>
              <th className="px-3 py-2">E-Mail</th>
              <th className="px-3 py-2">Provider</th>
              <th className="px-3 py-2">Abo</th>
              <th className="px-3 py-2 text-center">Drohnen</th>
              <th className="px-3 py-2 text-center">Flüge</th>
              <th className="px-3 py-2 text-center">Logbuch</th>
              <th className="px-3 py-2">Registriert</th>
              <th className="px-3 py-2 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSelf = u.id === user.id;
              const rowBusy = busy === u.id;
              return (
                <tr key={u.id} className="border-t border-[var(--color-app-border)] hover:bg-[var(--color-app-bg-secondary)]">
                  <td className="px-3 py-2">
                    {u.name || <span className="text-[var(--color-app-text-tertiary)]">—</span>}
                    {u.isAdmin && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--color-caution-bg)] text-[var(--color-caution)] text-[10px] font-semibold uppercase">
                        Admin
                      </span>
                    )}
                    {u.needsPasswordSetup && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--color-caution-bg)] text-[var(--color-caution)] text-[10px] font-semibold uppercase">
                        Passwort offen
                      </span>
                    )}
                    {isSelf && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--color-brand-50)] text-[var(--color-brand-600)] text-[10px] font-semibold uppercase">
                        Du
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-app-text-secondary)]">{u.authProvider}</td>
                  <td className="px-3 py-2">
                    {u.subscription?.active ? (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded bg-[var(--color-go-bg)] text-[var(--color-go)] text-[10px] font-semibold uppercase"
                        title={`${SUB_SOURCE_LABEL[u.subscription.source] ?? u.subscription.source}${u.subscription.planName ? ` · ${u.subscription.planName}` : ""}`}
                        data-testid={`badge-sub-${u.id}`}
                      >
                        {u.subscription.planName || SUB_SOURCE_LABEL[u.subscription.source] || "Aktiv"}
                      </span>
                    ) : (
                      <span className="text-xs text-[var(--color-app-text-tertiary)]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{u.counts.drones}</td>
                  <td className="px-3 py-2 text-center">{u.counts.sessions}</td>
                  <td className="px-3 py-2 text-center">{u.counts.logbook}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-app-text-secondary)]">
                    {new Date(u.createdAt).toLocaleDateString("de-DE")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex flex-wrap gap-1 justify-end">
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="px-2 py-1 text-xs bg-[var(--color-app-bg-tertiary)] hover:bg-[var(--color-app-bg-tertiary)] rounded"
                      >
                        Bearbeiten
                      </Link>
                      <button
                        disabled={rowBusy || (isSelf && u.isAdmin)}
                        onClick={() => toggleRole(u)}
                        title={
                          isSelf && u.isAdmin
                            ? "Du kannst dir selbst die Admin-Rolle nicht entziehen"
                            : undefined
                        }
                        className="px-2 py-1 text-xs bg-[var(--color-caution-bg)] hover:bg-[var(--color-caution-bg)] text-[var(--color-caution)] rounded disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {u.isAdmin ? "Admin entziehen" : "Zum Admin"}
                      </button>
                      {!u.needsPasswordSetup && (
                        <button
                          disabled={rowBusy}
                          onClick={() => resetPassword(u)}
                          className="px-2 py-1 text-xs bg-[var(--color-brand-50)] hover:bg-[var(--color-brand-50)] text-[var(--color-brand-600)] rounded disabled:opacity-40"
                        >
                          Passwort zurücksetzen
                        </button>
                      )}
                      {u.needsPasswordSetup && (
                        <button
                          disabled={rowBusy}
                          onClick={() => issueSetupLink(u)}
                          className="px-2 py-1 text-xs bg-[var(--color-brand-50)] hover:bg-[var(--color-brand-50)] text-[var(--color-brand-600)] rounded disabled:opacity-40"
                        >
                          Setup-Link neu ausstellen
                        </button>
                      )}
                      <button
                        disabled={rowBusy || isSelf}
                        onClick={() => deleteUser(u)}
                        title={isSelf ? "Du kannst dich nicht selbst löschen" : undefined}
                        className="px-2 py-1 text-xs bg-[var(--color-nogo-bg)] hover:bg-[var(--color-nogo-bg)] text-[var(--color-nogo)] rounded disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-[var(--color-app-text-tertiary)]">
                  Keine Nutzer gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pwModal && (
        <PasswordModal
          email={pwModal.email}
          password={pwModal.password}
          onClose={() => setPwModal(null)}
        />
      )}
      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4 shadow-xl">
            <h2 className="text-lg font-semibold text-[var(--color-app-text)]">
              Nutzerkonto anlegen
            </h2>
            <p className="text-sm text-[var(--color-app-text-secondary)]">
              Es wird ein normales Nutzerkonto ohne Admin-Rechte angelegt. Der einmalige
              Passwort-Setup-Link wird anschließend hier angezeigt; es wird keine E-Mail versendet.
            </p>
            <form onSubmit={createUser} className="space-y-3">
              <label className="block text-sm font-medium text-[var(--color-app-text)]">
                E-Mail-Adresse
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={createEmail}
                  onChange={(event) => setCreateEmail(event.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm"
                />
              </label>
              <label className="block text-sm font-medium text-[var(--color-app-text)]">
                Name (optional)
                <input
                  type="text"
                  maxLength={200}
                  autoComplete="name"
                  value={createName}
                  onChange={(event) => setCreateName(event.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm"
                />
              </label>
              {createError && (
                <p role="alert" className="text-sm text-[var(--color-nogo)]">
                  {createError}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  disabled={busy === "create"}
                  onClick={() => setCreateOpen(false)}
                  className="px-3 py-2 text-sm bg-[var(--color-app-bg-tertiary)] rounded disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={busy === "create"}
                  className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded disabled:opacity-50"
                >
                  {busy === "create" ? "Wird angelegt …" : "Nutzer anlegen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {setupLink && (
        <SetupLinkModal
          email={setupLink.email}
          url={setupLink.url}
          expiresAt={setupLink.expiresAt}
          onClose={() => setSetupLink(null)}
        />
      )}
    </div>
  );
}
